/**
 * Reports: item analysis of one course version (plan docs/plans/lms-plan.md
 * 13 tab 6 "item analysis per course version: per question the answer
 * distribution and the miss rate, so a question everyone misses is found";
 * Appendix A GET /admin/reports/item-analysis/:versionId; 18.23; R21; WP-45).
 *
 * Pick a course (one with a published version and a quiz: read-and-acknowledge
 * courses have no questions), then a version (the current one first; drafts
 * are never analysed, nobody sat them). For every question of the version, in
 * authored order or most missed first: how many submitted attempts drew it,
 * answered it right and missed it, the miss rate, and per option how often it
 * was chosen, drawn as a bar against the times shown. The correct options are
 * marked with a word and an icon (color never alone). Attempts by people the
 * admin does not reach are left out and counted (18.23).
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ListChecks } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import FilterSelect from '../../../../ui/FilterSelect';
import { listAdminCourses } from '../../../api/assignments';
import { listCourseVersions } from '../../../api/catalog';
import { getItemAnalysis } from '../../../api/reports';
import type { ItemQuestion } from '../../../api/reports';
import { Pill } from '../../../components/StatusPill';
import type { PillTone } from '../../../components/StatusPill';
import { cardCls, checkboxCls, formatDay, labelCls, metaCls, microLabelCls } from '../../../components/pickers/shared';
import { LoadFailed, Loading, useAsync } from '../matrix/shared';

/** The miss rate as a pill: half or more missed reads red, a quarter amber (a question to look at, R21). */
export function missTone(rate: number | null): PillTone {
  if (rate === null) return 'gray';
  if (rate >= 50) return 'red';
  if (rate >= 25) return 'amber';
  return 'green';
}

/** An option's share of the times the question was shown, as a whole percentage (0 when never shown). */
export function chosenShare(chosen: number, shown: number): number {
  if (!shown) return 0;
  return Math.min(100, Math.round((chosen * 100) / shown));
}

const KIND_WORDS: Record<string, string> = {
  single: 'One answer',
  multi: 'Several answers',
  true_false: 'True or false',
};

function QuestionCard({ q }: { q: ItemQuestion }) {
  return (
    <li className={cardCls} data-testid={`item-question-${q.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <span className={microLabelCls}>{`Question ${q.ordinal} \u00b7 ${KIND_WORDS[q.kind] ?? q.kind}${q.difficulty ? ` \u00b7 ${q.difficulty}` : ''}`}</span>
          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{q.stem}</p>
        </div>
        <Pill tone={missTone(q.miss_rate)} data-testid={`item-miss-${q.id}`}>
          {q.miss_rate === null ? 'Not drawn yet' : `Missed by ${q.miss_rate}%`}
        </Pill>
      </div>
      <p className={`${metaCls} mt-1`}>
        {`Shown ${q.shown.toLocaleString()} times, answered right ${q.correct.toLocaleString()}, missed ${q.missed.toLocaleString()}.`}
      </p>
      <ul className="mt-3 space-y-2">
        {q.options.map((o) => {
          const share = chosenShare(o.chosen, q.shown);
          return (
            <li key={o.id} data-testid={`item-option-${q.id}-${o.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 text-gray-800 dark:text-gray-200">
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{`${o.id.toUpperCase()}. `}</span>
                  {o.text}
                  {o.correct ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Correct
                    </span>
                  ) : null}
                </span>
                <span className="tabular-nums text-xs text-gray-600 dark:text-gray-300" data-testid={`item-chosen-${q.id}-${o.id}`}>
                  {`${o.chosen.toLocaleString()} chose (${share}%)`}
                </span>
              </div>
              <div
                className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
                role="img"
                aria-label={`${share}% of attempts chose option ${o.id.toUpperCase()}`}
              >
                <div
                  className={`h-full rounded-full ${o.correct ? 'bg-green-500 dark:bg-green-400' : 'bg-gray-400 dark:bg-gray-500'}`}
                  style={{ width: `${share}%` }}
                  data-testid={`item-bar-${q.id}-${o.id}`}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </li>
  );
}

export function ItemAnalysisPanel() {
  const [courseId, setCourseId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [mostMissed, setMostMissed] = useState(false);

  const courses = useAsync(() => listAdminCourses(), 'courses', 'The courses could not be loaded.');
  const quizCourses = useMemo(
    () => (courses.data ?? []).filter((c) => c.kind !== 'read_ack' && c.current_version !== null),
    [courses.data]
  );
  const versions = useAsync(() => listCourseVersions(courseId), courseId, 'The versions of this course could not be loaded.', Boolean(courseId));
  const published = useMemo(() => (versions.data ?? []).filter((v) => v.status !== 'draft'), [versions.data]);

  // A new course starts on its current version.
  useEffect(() => {
    const course = quizCourses.find((c) => c.id === courseId);
    setVersionId(course?.current_version?.id ?? '');
  }, [courseId, quizCourses]);

  const analysis = useAsync(() => getItemAnalysis(versionId), versionId, 'The item analysis could not be loaded.', Boolean(versionId));
  const questions = useMemo(() => {
    const list = analysis.data?.questions ?? [];
    if (!mostMissed) return list;
    return list.slice().sort((a, b) => (b.miss_rate ?? -1) - (a.miss_rate ?? -1) || a.ordinal - b.ordinal);
  }, [analysis.data, mostMissed]);

  return (
    <div className="space-y-4" data-testid="report-items">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <span className={labelCls}>Course</span>
          <FilterSelect label="Course" value={courseId} onChange={setCourseId} auto disabled={courses.loading}>
            <option value="">Choose a course</option>
            {quizCourses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </FilterSelect>
        </div>
        {courseId ? (
          <div>
            <span className={labelCls}>Version</span>
            <FilterSelect label="Version" value={versionId} onChange={setVersionId} auto disabled={versions.loading}>
              {published.length === 0 ? <option value={versionId}>Current version</option> : null}
              {published.map((v) => (
                <option key={v.id} value={v.id}>
                  {`Version ${v.version_no}${v.status === 'published' ? ' (current)' : ''}${v.published_at ? `, published ${formatDay(v.published_at)}` : ''}`}
                </option>
              ))}
            </FilterSelect>
          </div>
        ) : null}
        {analysis.data ? (
          <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
            <input type="checkbox" className={checkboxCls} checked={mostMissed} onChange={(e) => setMostMissed(e.target.checked)} data-testid="items-most-missed" />
            Most missed first
          </label>
        ) : null}
      </div>

      {courses.error ? <LoadFailed message={courses.error} onRetry={courses.reload} /> : null}
      {versions.error ? <LoadFailed message={versions.error} onRetry={versions.reload} /> : null}
      {analysis.error ? <LoadFailed message={analysis.error} onRetry={analysis.reload} testId="items-error" /> : null}

      {!courseId ? (
        <EmptyState icon={ListChecks} variant="card" title="Choose a course" hint="See how every question of a version was answered, and find the ones people miss." />
      ) : null}
      {courseId && analysis.loading && !analysis.data ? <Loading rows={4} /> : null}

      {analysis.data ? (
        <>
          <p className={metaCls} data-testid="items-summary">
            {`${analysis.data.course.title}, version ${analysis.data.version.version_no}: ${analysis.data.attempts.submitted.toLocaleString()} submitted attempts (${analysis.data.attempts.passed.toLocaleString()} passed, ${analysis.data.attempts.failed.toLocaleString()} failed).`}
            {analysis.data.attempts.out_of_reach > 0
              ? ` ${analysis.data.attempts.out_of_reach.toLocaleString()} ${analysis.data.attempts.out_of_reach === 1 ? 'attempt' : 'attempts'} by people outside your sites ${analysis.data.attempts.out_of_reach === 1 ? 'is' : 'are'} left out.`
              : ''}
          </p>
          {questions.length === 0 ? (
            <EmptyState icon={ListChecks} variant="card" title="This version has no questions" />
          ) : (
            <ol className="space-y-3" data-testid="items-list">
              {questions.map((q) => (
                <QuestionCard key={q.id} q={q} />
              ))}
            </ol>
          )}
        </>
      ) : null}
    </div>
  );
}

export default ItemAnalysisPanel;
